import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Props = {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
};

export default function AuthShell({ title, subtitle, children, footer }: Props) {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-cream-50">
      {/* Brand panel */}
      <aside
        className="relative hidden lg:flex w-[44%] xl:w-2/5 text-white flex-col justify-between p-10 xl:p-14 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Stylized%20cinematic%20golden-hour%20warm%20painterly%20illustration%20of%20professional%20moving%20crew%20loading%20a%20deep%20orange%20vintage%20moving%20truck%20outside%20a%20cream%20brownstone%20home%2C%20rich%20amber%20and%20cream%20color%20palette%2C%20soft%20diffused%20sunset%20light%2C%20artistic%20and%20textured%20look%20with%20painterly%20brush%20feel%2C%20warm%20earthy%20tones%2C%20no%20text%2C%20clean%20simple%20background%20with%20soft%20foliage%20and%20sidewalk%2C%20editorial%20quality%2C%20no%20realistic%20photo%20feel&width=1200&height=1800&seq=auth-side-001&orientation=portrait')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-ink-900/80 via-ink-900/55 to-ink-900/85"></div>

        <div className="relative z-10 flex items-center gap-2.5">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white text-ink-900">
            <i className="ri-truck-fill text-xl"></i>
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-bold">Haulmark</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">
              Operations Portal
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-semibold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            Crew portal
          </span>
          <h2 className="font-display text-3xl xl:text-4xl font-bold leading-tight mt-5">
            Run the whole moving day from one screen.
          </h2>
          <p className="text-white/75 text-sm mt-3 leading-relaxed">
            Dispatch trucks, track crews, send quotes, and watch payments roll in — all in one
            place. Built for the team behind every move.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { v: "38", l: "Crew members" },
              { v: "12.4k", l: "Moves logged" },
              { v: "4.9★", l: "Avg rating" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-2xl font-bold">{s.v}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[11px] text-white/60">
          © Haulmark Movers · USDOT 3417xx · MC 1129xx
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex flex-col">
        <header className="flex items-center justify-between px-6 lg:px-10 py-5">
          <Link to="/" className="flex items-center gap-2.5 lg:hidden">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-ink-900 text-white">
              <i className="ri-truck-fill text-lg"></i>
            </div>
            <span className="font-display text-base font-bold text-ink-900">Haulmark</span>
          </Link>
          <Link
            to="/"
            className="ml-auto text-sm font-semibold text-ink-500 hover:text-ink-900 inline-flex items-center gap-1.5 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to site
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-6 lg:px-10 pb-10">
          <div className="w-full max-w-md">
            <h1 className="font-display text-3xl md:text-[34px] font-bold text-ink-900 leading-tight">
              {title}
            </h1>
            <p className="text-sm text-ink-500 mt-2">{subtitle}</p>

            <div className="mt-8">{children}</div>

            <div className="mt-8 pt-6 border-t border-ink-100 text-sm text-ink-500">{footer}</div>
          </div>
        </div>
      </main>
    </div>
  );
}